/**
 * CSS Perspective Transform Utilities
 * Converts HomographyCorners to CSS matrix3d() for perspective placement.
 *
 * The approach: compute a 3x3 homography mapping the source rectangle to the
 * destination quadrilateral (in pixel coordinates), then embed it into
 * CSS matrix3d format.
 */

import type { HomographyCorners, Point2D } from '@frameit/types';

/**
 * Compute CSS matrix3d() from normalized corners and container dimensions.
 *
 * @param corners Placement zone corners in normalized (0-1) coordinates
 * @param containerWidth Container width in pixels
 * @param containerHeight Container height in pixels
 * @param elementWidth Element width in pixels (the framed artwork)
 * @param elementHeight Element height in pixels
 * @returns CSS transform string with matrix3d()
 */
export function computeCSS3DMatrix(
  corners: HomographyCorners,
  containerWidth: number,
  containerHeight: number,
  elementWidth: number,
  elementHeight: number,
): string {
  // Validate dimensions — zero/negative values produce NaN/Infinity in matrix
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    elementWidth <= 0 ||
    elementHeight <= 0 ||
    !isFinite(containerWidth) ||
    !isFinite(containerHeight) ||
    !isFinite(elementWidth) ||
    !isFinite(elementHeight)
  ) {
    return '';
  }

  // Convert normalized corners to pixel coordinates
  const dst: [number, number][] = [
    [corners.topLeft.x * containerWidth, corners.topLeft.y * containerHeight],
    [corners.topRight.x * containerWidth, corners.topRight.y * containerHeight],
    [corners.bottomRight.x * containerWidth, corners.bottomRight.y * containerHeight],
    [corners.bottomLeft.x * containerWidth, corners.bottomLeft.y * containerHeight],
  ];

  // Source rectangle: the element at origin
  const src: [number, number][] = [
    [0, 0],
    [elementWidth, 0],
    [elementWidth, elementHeight],
    [0, elementHeight],
  ];

  // Compute 3x3 homography mapping src → dst
  const H = computeHomography3x3(src, dst);
  if (!H) return '';

  // Validate matrix values — reject if any value is NaN/Infinity or unreasonably large
  for (const val of H) {
    if (!isFinite(val) || Math.abs(val) > 1e6) return '';
  }

  // Convert to CSS matrix3d
  // CSS matrix3d is column-major 4x4:
  // matrix3d(a1,a2,a3,a4, b1,b2,b3,b4, c1,c2,c3,c4, d1,d2,d3,d4)
  //
  // For a 2D homography [h0,h1,h2; h3,h4,h5; h6,h7,h8] embedded in 3D:
  // | h0  h1  0  h2 |
  // | h3  h4  0  h5 |
  // |  0   0  1   0 |
  // | h6  h7  0  h8 |
  //
  // In CSS column-major order:
  const m = [H[0], H[3], 0, H[6], H[1], H[4], 0, H[7], 0, 0, 1, 0, H[2], H[5], 0, H[8]];

  return `matrix3d(${m.join(',')})`;
}

/**
 * Compute 3x3 homography matrix mapping 4 source points to 4 destination points.
 * Solves the 8×8 linear system directly via Gaussian elimination (with h8 = 1).
 *
 * The homography maps (sx,sy) → (dx,dy) via:
 *   dx = (h0·sx + h1·sy + h2) / (h6·sx + h7·sy + 1)
 *   dy = (h3·sx + h4·sy + h5) / (h6·sx + h7·sy + 1)
 */
function computeHomography3x3(src: [number, number][], dst: [number, number][]): number[] | null {
  // Build 8×8 system: A·h = b, where h = [h0,h1,h2,h3,h4,h5,h6,h7], h8 = 1
  //
  // For each point pair (sx,sy)→(dx,dy):
  //   h0·sx + h1·sy + h2 - h6·dx·sx - h7·dx·sy = dx
  //   h3·sx + h4·sy + h5 - h6·dy·sx - h7·dy·sy = dy
  const N = 8;
  const M: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i++) {
    const [sx, sy] = src[i];
    const [dx, dy] = dst[i];

    M.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
    b.push(dx);
    M.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
    b.push(dy);
  }

  // Gaussian elimination with partial pivoting
  const aug = M.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < N; col++) {
    // Find pivot row
    let maxVal = Math.abs(aug[col][col]);
    let maxRow = col;
    for (let row = col + 1; row < N; row++) {
      const val = Math.abs(aug[row][col]);
      if (val > maxVal) {
        maxVal = val;
        maxRow = row;
      }
    }

    if (maxVal < 1e-12) return null; // Singular matrix

    // Swap rows
    if (maxRow !== col) {
      [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
    }

    // Eliminate below
    const pivot = aug[col][col];
    for (let row = col + 1; row < N; row++) {
      const factor = aug[row][col] / pivot;
      for (let j = col; j <= N; j++) {
        aug[row][j] -= factor * aug[col][j];
      }
    }
  }

  // Back substitution
  const h = new Array(N).fill(0);
  for (let row = N - 1; row >= 0; row--) {
    let sum = aug[row][N];
    for (let col = row + 1; col < N; col++) {
      sum -= aug[row][col] * h[col];
    }
    if (Math.abs(aug[row][row]) < 1e-12) return null;
    h[row] = sum / aug[row][row];
  }

  // Return full 9-element homography with h8 = 1
  return [...h, 1];
}

/**
 * Compute placement corners for artwork within a zone,
 * respecting aspect ratio and applying scale/offset.
 *
 * @param zone The placement zone corners (normalized 0-1)
 * @param artworkAspect Width/height ratio of the framed artwork
 * @param scale Scale factor (0-1, relative to zone)
 * @param offset Offset from zone center (normalized)
 * @returns Corners for the artwork in normalized coordinates
 */
export function computeArtworkCorners(
  zone: HomographyCorners,
  artworkAspect: number,
  scale: number,
  offset: Point2D,
): HomographyCorners {
  const { topLeft: tl, topRight: tr, bottomRight: br, bottomLeft: bl } = zone;

  // Compute zone dimensions (approximate from corners)
  const zoneWidth = Math.sqrt((tr.x - tl.x) ** 2 + (tr.y - tl.y) ** 2);
  const zoneHeight = Math.sqrt((bl.x - tl.x) ** 2 + (bl.y - tl.y) ** 2);
  const zoneAspect = zoneWidth / zoneHeight;

  // Compute artwork size within zone
  let artU: number, artV: number;
  if (artworkAspect > zoneAspect) {
    artU = scale;
    artV = (scale * zoneAspect) / artworkAspect;
  } else {
    artV = scale;
    artU = (scale * artworkAspect) / zoneAspect;
  }

  // Center + offset (offset range roughly -0.5 to 0.5)
  const centerU = 0.5 + offset.x * 0.5;
  const centerV = 0.5 + offset.y * 0.5;

  const uMin = centerU - artU / 2;
  const uMax = centerU + artU / 2;
  const vMin = centerV - artV / 2;
  const vMax = centerV + artV / 2;

  // Bilinear interpolation within the zone quad
  const lerp = (p1: Point2D, p2: Point2D, t: number): Point2D => ({
    x: p1.x + (p2.x - p1.x) * t,
    y: p1.y + (p2.y - p1.y) * t,
  });

  const bilinear = (u: number, v: number): Point2D => {
    const top = lerp(tl, tr, u);
    const bottom = lerp(bl, br, u);
    return lerp(top, bottom, v);
  };

  return {
    topLeft: bilinear(uMin, vMin),
    topRight: bilinear(uMax, vMin),
    bottomRight: bilinear(uMax, vMax),
    bottomLeft: bilinear(uMin, vMax),
  };
}
